import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { useToast } from "../components/ui/Toast";
import { useAdaptersApi } from '../apis/adapters-apis';
import { Adapter } from '../types';
import { Eye, Plus, Search, Trash2, ChevronUp, Loader2 } from 'lucide-react';
import _ from 'lodash';

const ITEMS_PER_PAGE = 10;

export const AdaptersList: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const adaptersApi = useAdaptersApi();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(false);

  // State management similar to SkillsList
  const [adapters, setAdapters] = useState<Adapter[]>([]);
  const [displayedAdapters, setDisplayedAdapters] = useState<Adapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Adapter[] | null>(null);
  
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adapterToDelete, setAdapterToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  // Initial data fetch
  useEffect(() => {
    const controller = new AbortController();

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const getAllAdapters = false;
        const fetchedAdapters = await adaptersApi.getAdapters();
        
        if (isMounted.current) {
          setAdapters(fetchedAdapters);
          setTotalCount(fetchedAdapters.length);
          setDisplayedAdapters(fetchedAdapters.slice(0, ITEMS_PER_PAGE));
          setHasMore(fetchedAdapters.length > ITEMS_PER_PAGE);
        }
      } catch (error) {
        if (isMounted.current && error instanceof Error && error.name !== 'AbortError') {
          console.error('Error fetching adapters:', error);
          addToast({
            title: "Error",
            description: "Failed to retrieve adapters. Please try again.",
            variant: "destructive",
          });
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    isMounted.current = true;
    fetchInitialData();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, []);

  const loadMoreAdapters = () => {
    if (searchTerm || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const startIndex = displayedAdapters.length;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    
    if (startIndex >= adapters.length) {
      setHasMore(false);
      setLoadingMore(false);
      return;
    }

    const newItems = adapters.slice(startIndex, endIndex);
    setDisplayedAdapters(prev => [...prev, ...newItems]);
    
    if (endIndex >= adapters.length) {
      setHasMore(false);
    }

    setLoadingMore(false);
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !searchTerm) {
        loadMoreAdapters();
      }
    }, options);

    const loader = document.querySelector('#scroll-loader');
    if (loader) {
      observer.observe(loader);
    }

    return () => observer.disconnect();
  }, [loadMoreAdapters, hasMore, loadingMore, searchTerm]);

  const handleSearch = _.debounce((term: string) => {
    setSearchTerm(term);
    if (term) {
      const results = adapters.filter(adapter =>
        adapter.adapterName.toLowerCase().includes(term.toLowerCase()) ||
        adapter.adapterDescription?.toLowerCase().includes(term.toLowerCase()) ||
        adapter.adapterTags?.some(tag => tag.toLowerCase().includes(term.toLowerCase()))
      );
      setSearchResults(results);
    } else {
      setSearchResults(null);
      setDisplayedAdapters(adapters.slice(0, ITEMS_PER_PAGE));
      setHasMore(adapters.length > ITEMS_PER_PAGE);
    }
  }, 300);

  const handleViewAdapter = (adapterId: string) => {
    navigate(`/marketplace/admin/adapters/edit/${adapterId}`);
  };

  const handleDeleteClick = (id: string) => {
    setAdapterToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!adapterToDelete) return;

    setIsDeletingId(adapterToDelete);
    try {
      await adaptersApi.deleteAdapter(adapterToDelete);
      addToast({
        title: "Success",
        description: "Adapter successfully deleted",
        variant: "default",
      });
      
      // Refresh the list
      const getAllAdapters = false;
      const fetchedAdapters = await adaptersApi.getAdapters();
      setAdapters(fetchedAdapters);
      setTotalCount(fetchedAdapters.length);
      setDisplayedAdapters(fetchedAdapters.slice(0, ITEMS_PER_PAGE));
      setHasMore(fetchedAdapters.length > ITEMS_PER_PAGE);
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to delete adapter. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setAdapterToDelete(null);
    }
  };

  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setShowScrollTop(window.scrollY > 400);
    }, 200);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentAdapters = searchResults || displayedAdapters;
  const currentTotal = searchResults ? searchResults.length : totalCount;

  return (
    <div className="container mx-auto p-4" ref={containerRef}>
      <h1 className="text-2xl mb-4">
        Adapters Management
      </h1>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-muted-foreground" size={20} />
          <Input
            type="text"
            placeholder="Search adapters..."
            className="w-full pl-10"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      <Button
        variant="default"
        className="mb-4 bg-green-500 hover:bg-green-600 text-white"
        onClick={() => navigate('/marketplace/admin/adapters/add')}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add New Adapter
      </Button>

      <div className="text-sm text-muted-foreground mb-4">
        {searchTerm ? (
          `Found ${currentAdapters.length || 0} results for "${searchTerm}"`
        ) : (
          `Showing ${currentAdapters.length} of ${currentTotal} adapters`
        )}
      </div>
      <div className="grid gap-4">
        {currentAdapters.map((adapter) => (
          <Card key={adapter.adapterId} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-grow">
                  <h2 className="text-xl font-semibold">{adapter.adapterName}</h2>
                  <p className="text-muted-foreground mt-2">{adapter.adapterDescription}</p>
                  {adapter.adapterTags && adapter.adapterTags.length > 0 && (
                    <div className="relative">
                      <div className="flex flex-wrap gap-1 mt-2">
                        {adapter.adapterTags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20"
                          >
                            {tag}
                          </span>
                        ))}
                        {adapter.adapterTags.length > 3 && (
                          <div className="relative inline-block group">
                            <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20 cursor-pointer">
                              +{adapter.adapterTags.length - 3}
                            </span>
                            <div className="absolute left-0 mt-2 p-2 bg-white border border-gray-200 rounded-md shadow-lg z-10 hidden group-hover:block">
                              {adapter.adapterTags.slice(3).map((tag, index) => (
                                <span
                                  key={index}
                                  className="block text-xs text-gray-700"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="secondary"
                    onClick={() => handleViewAdapter(adapter.adapterId || '')}
                    className="bg-blue-100 hover:bg-blue-200 text-blue-600"
                    disabled={isDeletingId === adapter.adapterId}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleDeleteClick(adapter.adapterId || '')}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    disabled={isDeletingId === adapter.adapterId}
                  >
                    {isDeletingId === adapter.adapterId ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {!searchTerm && hasMore && (
        <div id="scroll-loader" className="h-10 w-full" />
      )}

      {loading ? (
        <div className="flex flex-col items-center my-8 gap-2">
          <Spinner className="border-blue-500" />
          <p className="text-center text-muted-foreground">Loading Adapters...</p>
        </div>
      ) : loadingMore ? (
        <div className="flex justify-center my-4">
          <Spinner className="border-blue-500" />
        </div>
      ) : (!loading && currentAdapters.length === 0) && (
        <Card className="p-8">
          <p className="text-center text-muted-foreground">
            {searchTerm ? 'No adapters found matching your search.' : 'No adapters found. Create a new adapter to get started.'}
          </p>
        </Card>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Adapter"
        description="This will permanently delete the adapter from the system. This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white"
        >
          <ChevronUp size={24} />
        </Button>
      )}
    </div>
  );
};

export default AdaptersList;